import {
    formatChartTokenAmount,
    formatCompactNumber
} from "@solomon-apps/shared/src/components/Charts/chartFormat";
import {
    INLINE_CHART_STYLE,
    InlineInspectionStyle,
    InlineReadout,
    type InlineReadoutRow,
    InlineReadoutSpacer,
    squareActiveDot,
    useChartInspection
} from "@solomon-apps/shared/src/components/Charts/chartInspection";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import React, { useMemo } from "react";
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from "recharts";

import { formatLongDate, formatShortDate } from "../../shared/format";
import { niceTicks } from "../chartAxis";
import {
    hasChartHistory,
    type ReportingRange,
    windowByRange
} from "../chartVisibility";
import { GRAPH_BG_CLASS, graphColor } from "../graphColors";
import {
    type ReportRow,
    type ReportSeries,
    reportValues,
    type StackedReport,
    TOTAL_SLOT
} from "../reportingSeries";

import { ReportPanel, SparseRangeNote } from "./ReportPanel";

const CHART_HEIGHT = 240;
const AXIS_TICK = {
    fill: "rgb(var(--color-foreground-base) / 0.6)",
    fontSize: 11,
    fontFamily: "ABC Social"
};

function formatUsdv(value: number): string {
    return formatChartTokenAmount(value, "USDv");
}

/** One-sentence reading of the chart for assistive tech: span + latest split. */
export function describeStackedReport(
    title: string,
    series: readonly ReportSeries[],
    rows: readonly ReportRow[]
): string {
    const first = rows[0];
    const last = rows.filter((row) => row.total !== null).at(-1);
    if (!first || !last) return title;
    const parts = series
        .map((s) => `${s.label} ${formatUsdv(last[s.key] ?? 0)}`)
        .join(", ");
    const split = series.length > 1 ? `: ${parts}` : "";
    return `${title}, ${formatLongDate(first.t)} to ${formatLongDate(last.t)}. Latest total ${formatUsdv(last.total!)}${split}.`;
}

/**
 * A USDv time series stacked by position: the Earnings (Cumulative) and
 * Average Qualifying Balance panels. Stacked areas because each band is a
 * position's share and the top edge is the portfolio total. Flat fills, no
 * gradient. Hover is the standard inline inspection (`chartInspection`):
 * every position, top of the stack first, then the total.
 */
export function StackedAreaReport({
    title,
    hint,
    action,
    report,
    range,
    curve
}: {
    title: string;
    hint?: string;
    action?: React.ReactNode;
    report: StackedReport;
    range: ReportingRange;
    /** `stepAfter` for payouts (totals move only on payout days); `linear` for settlement-period balances. */
    curve: "stepAfter" | "linear";
}): React.ReactElement {
    const rows = useMemo(
        () => windowByRange(report.rows, range),
        [report.rows, range]
    );
    const inRange = hasChartHistory(reportValues(rows));
    const ticks = useMemo(
        () => niceTicks(Math.max(0, ...rows.map((row) => row.total ?? 0))),
        [rows]
    );
    const { series } = report;
    const inspection = useChartInspection();
    const readoutRowCount =
        series.length > 1 ? series.length + 1 : series.length;

    return (
        <ReportPanel title={title} hint={hint} action={action}>
            {inRange ? (
                <div
                    className="pt-4 pb-2 select-none"
                    {...inspection.containerProps}
                >
                    <InlineInspectionStyle />
                    <p className="sr-only">
                        {describeStackedReport(title, series, rows)}
                    </p>
                    <InlineReadoutSpacer rows={readoutRowCount} />
                    <ResponsiveContainer
                        width="100%"
                        height={CHART_HEIGHT}
                        minWidth={0}
                    >
                        <AreaChart
                            data={rows}
                            margin={{ top: 8, right: 16, left: 8, bottom: 4 }}
                            style={INLINE_CHART_STYLE}
                        >
                            <CartesianGrid
                                horizontal
                                vertical={false}
                                stroke="rgb(var(--color-foreground-base) / 0.08)"
                            />
                            <XAxis
                                dataKey="t"
                                type="number"
                                scale="time"
                                domain={["dataMin", "dataMax"]}
                                tickFormatter={(v: number) =>
                                    formatShortDate(v)
                                }
                                axisLine={false}
                                tickLine={false}
                                tick={AXIS_TICK}
                                minTickGap={48}
                                padding={{ left: 8, right: 8 }}
                            />
                            <YAxis
                                domain={[0, ticks[ticks.length - 1]]}
                                ticks={ticks}
                                tickFormatter={(v: number) =>
                                    formatCompactNumber(v)
                                }
                                axisLine={false}
                                tickLine={false}
                                width={56}
                                tick={AXIS_TICK}
                            />
                            <Tooltip
                                {...inspection.tooltipProps}
                                content={(props) => {
                                    const p = props as unknown as {
                                        active?: boolean;
                                        payload?: ReadonlyArray<{
                                            payload?: ReportRow;
                                        }>;
                                    };
                                    const row = p.payload?.[0]?.payload;
                                    return (
                                        <InlineReadout
                                            active={Boolean(p.active)}
                                            x={props.coordinate?.x}
                                            rows={readoutRows(series, row)}
                                            date={
                                                row
                                                    ? formatLongDate(row.t)
                                                    : null
                                            }
                                            onActiveChange={
                                                inspection.setInspecting
                                            }
                                        />
                                    );
                                }}
                            />
                            {series.map((s) => (
                                <Area
                                    key={s.key}
                                    type={curve}
                                    connectNulls={false}
                                    dataKey={s.key}
                                    name={s.label}
                                    stackId="report"
                                    stroke={graphColor(s.slot)}
                                    strokeWidth={1.5}
                                    fill={graphColor(s.slot)}
                                    // A lone olive total reads as a slab at the
                                    // position bands' strength; keep it a wash.
                                    fillOpacity={
                                        s.slot === TOTAL_SLOT ? 0.2 : 0.55
                                    }
                                    dot={false}
                                    activeDot={squareActiveDot(
                                        graphColor(s.slot)
                                    )}
                                    isAnimationActive
                                    animationDuration={400}
                                />
                            ))}
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            ) : (
                <SparseRangeNote />
            )}

            <Text
                variant="label"
                render={<ul />}
                className="pt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-foreground-muted"
                aria-label={`${title} series`}
            >
                {series.map((s) => (
                    <li key={s.key} className="inline-flex items-center gap-2">
                        <span
                            className={`size-2.5 ${GRAPH_BG_CLASS[s.slot]}`}
                            aria-hidden
                        />
                        <span>{s.label}</span>
                    </li>
                ))}
            </Text>
        </ReportPanel>
    );
}

/** Top of the stack first, so the readout reads in the order the bands do; then the total. */
function readoutRows(
    series: readonly ReportSeries[],
    row: ReportRow | undefined
): InlineReadoutRow[] {
    const rows: InlineReadoutRow[] = series
        .slice()
        .reverse()
        .map((s) => {
            const value = row?.[s.key] ?? null;
            return {
                key: s.key,
                name: s.label,
                color: graphColor(s.slot),
                value: value === null ? null : formatUsdv(value)
            };
        });
    if (series.length > 1) {
        const total = row?.total ?? null;
        rows.push({
            key: "total",
            name: "Total",
            value: total === null ? null : formatUsdv(total)
        });
    }
    return rows;
}
